import React from "react";
import * as Sentry from "@sentry/react";

const CHUNK_ERROR_RELOAD_KEY = "chunk-load-error-reload";

const isChunkLoadError = (error: Error): boolean =>
  error.name === "ChunkLoadError" ||
  /Loading (chunk|CSS chunk) \d+ failed/.test(error.message);

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
}

class ErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false };
  private clearReloadFlagTimeout?: number;

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidMount(): void {
    // Clear the guard only after the app has stayed mounted for a while,
    // so a second real ChunkLoadError still gets one retry instead of
    // looping if the flag were cleared immediately on every mount.
    this.clearReloadFlagTimeout = window.setTimeout(() => {
      sessionStorage.removeItem(CHUNK_ERROR_RELOAD_KEY);
    }, 10000);
  }

  componentWillUnmount(): void {
    window.clearTimeout(this.clearReloadFlagTimeout);
  }

  componentDidCatch(error: Error): void {
    if (isChunkLoadError(error)) {
      const alreadyReloaded = sessionStorage.getItem(CHUNK_ERROR_RELOAD_KEY);

      if (!alreadyReloaded) {
        sessionStorage.setItem(CHUNK_ERROR_RELOAD_KEY, "1");
        window.location.reload();
        return;
      }
    }

    Sentry.captureException(error);
  }

  render(): React.ReactNode {
    if (this.state.hasError) {
      return (
        <div style={{ padding: 24, textAlign: "center" }}>
          <p>Something went wrong. Please refresh the page.</p>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
